/**
 * Check if a URL is a DIS (Dynamic Image Service) URL.
 * DIS URLs contain the pattern /dw/image/v2/ in their path.
 *
 * @param {string} url - The URL to check
 * @returns {boolean} True if URL is a DIS URL
 *
 * @example
 * isDisUrl('https://dev.stokke.com/dw/image/v2/aaqf_dev/on/demandware.static/-/image.jpg')
 * // Returns: true
 *
 * @example
 * isDisUrl('https://dev.stokke.com/on/demandware.static/-/image.jpg')
 * // Returns: false
 */
export function isDisUrl(url) {
    if (!url || typeof url !== 'string') {
        return false
    }
    return url.includes('/dw/image/v2/')
}

/**
 * Converts a Salesforce image URL to DIS (Dynamic Image Service) base URL.
 * Uses the sharedLibraryDis URL from OCAPI to extract the DIS base pattern.
 *
 * Follows Salesforce B2C Commerce DIS URL format:
 * http://<host>/dw/image/v2/<instance>/on/demandware.static/-/<image-path>
 *
 * @see https://help.salesforce.com/s/articleView?id=cc.b2c_creating_image_transformation_urls.htm&type=5
 *
 * @param {string} imageUrl - The image URL to convert
 * @param {string} [sharedLibraryDis] - The shared library DIS URL from OCAPI imageUrls.sharedLibraryDis
 * @returns {string|null} The DIS base URL or null if the URL is invalid
 *
 * @example
 * // Non-DIS to DIS using sharedLibraryDis
 * const imageUrl = 'https://dev.stokke.com/on/demandware.static/-/Library-Sites-StokkeSharedLibrary/default/dwc6da04cc/cms_assets/image.png'
 * const sharedLibraryDis = 'https://dev.stokke.com/dw/image/v2/aaqf_dev/on/demandware.jpg?sfrm=static/-/Library-Sites-StokkeSharedLibrary/en_GB/v1760971661941'
 * getDisBaseUrl(imageUrl, sharedLibraryDis)
 * // Returns: 'https://dev.stokke.com/dw/image/v2/aaqf_dev/on/demandware.static/-/Library-Sites-StokkeSharedLibrary/default/dwc6da04cc/cms_assets/image.png'
 *
 * @example
 * // Already DIS URL
 * getDisBaseUrl('https://dev.stokke.com/dw/image/v2/aaqf_dev/on/demandware.static/-/Library-Sites-StokkeSharedLibrary/default/dwc6da04cc/cms_assets/image.png')
 * // Returns: 'https://dev.stokke.com/dw/image/v2/aaqf_dev/on/demandware.static/-/Library-Sites-StokkeSharedLibrary/default/dwc6da04cc/cms_assets/image.png'
 */
export function getDisBaseUrl(imageUrl, sharedLibraryDis) {
    // Validate input
    if (!imageUrl || typeof imageUrl !== 'string') {
        return null
    }

    // Quick check: if already DIS URL, return unchanged (avoids URL parsing)
    if (isDisUrl(imageUrl)) {
        return imageUrl
    }

    // Try to parse image URL
    let url
    try {
        url = new URL(imageUrl)
    } catch {
        return null
    }

    // Check if it's a stokke domain
    if (!url.hostname.includes('stokke.com')) {
        return null
    }

    // Check if URL is in convertible non-DIS format
    const nonDisPattern = /^\/on\/demandware\.static\/-\//
    if (!nonDisPattern.test(url.pathname)) {
        // Not DIS and not convertible - return null
        return null
    }

    // URL is convertible - need sharedLibraryDis to convert
    if (!sharedLibraryDis || typeof sharedLibraryDis !== 'string') {
        return null
    }

    // Try to parse sharedLibraryDis URL
    let sharedLibraryUrl
    try {
        sharedLibraryUrl = new URL(sharedLibraryDis)
    } catch {
        return null
    }

    // Extract DIS base from sharedLibraryDis (everything up to /on/demandware.)
    const sharedLibraryPath = sharedLibraryUrl.pathname
    const demandwareIndex = sharedLibraryPath.indexOf('/on/demandware.')
    if (demandwareIndex === -1) {
        return null
    }

    const disBase = `${sharedLibraryUrl.protocol}//${
        sharedLibraryUrl.hostname
    }${sharedLibraryPath.substring(0, demandwareIndex)}/on/demandware.`

    // Extract the path after /on/demandware.static/-/
    const imagePath = url.pathname.replace(/^\/on\/demandware\.static\/-\//, '')

    // Construct DIS URL: disBase + static/-/ + imagePath
    return `${disBase}static/-/${imagePath}`
}
